import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { Box, CircularProgress, LinearProgress, createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { Suspense } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PERMISSIONS, ROLES } from './constants/roles';
import { SocketProvider } from './contexts/SocketContext';
import Sidebar from './components/Sidebar';
import PWAInstallBanner from './components/PWAInstallBanner';
// import Widgets from './components/Widgets'; // 遅延ロード用にコメントアウト
const Widgets = React.lazy(() => import('./components/Widgets'));
import ErrorNotification from './components/ErrorNotification';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import CorporateRegister from './pages/CorporateRegister';
import PosterRegister from './pages/PosterRegister';
import Profile from './pages/Profile';
import ProfileEdit from './pages/ProfileEdit';
import BlockedUsers from './pages/BlockedUsers';
import UnauthorizedPage from './pages/UnauthorizedPage';
import Explore from './pages/Explore';
import Notifications from './pages/Notifications';
import Bookmarks from './pages/Bookmarks';
import Lists from './pages/Lists';
import ListDetail from './pages/ListDetail';
import Shop from './pages/Shop';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import Ads from './pages/Ads';
import Connect from './pages/Connect';
import Messages from './pages/Messages';
import Stats from './pages/Stats';
import FollowList from './pages/FollowList';
import PostDetail from './pages/PostDetail';
import Dashboard from './pages/Dashboard';
import ReportForm from './pages/ReportForm';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import AuthAction from './pages/AuthAction';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// デバッグログは削除

// 企業ダッシュボード関連のインポート
import CorporateDashboard from './pages/corporate/Dashboard';
import Advertisements from './pages/corporate/Advertisements';
import TaggedUsers from './pages/corporate/TaggedUsers';
import CorporateProfileEdit from './pages/corporate/ProfileEdit';
import CorporateProfile from './pages/corporate/Profile';
import CorporateReservations from './pages/corporate/Reservations';
import Shifts from './pages/Shifts';
import Settings from './pages/Settings';
import Rankings from './pages/Rankings';
import ReviewsRanking from './pages/ReviewsRanking';
import ReviewDetails from './pages/ReviewDetails';
import ReservationDetail from './pages/ReservationDetail';
import PosterReservations from './pages/PosterReservations';

// デバッグログは削除

// ProtectedRouteコンポーネントの定義
const ProtectedRoute = ({ children, permission, allowGuest = false }) => {
  console.log('【デバッグ】ProtectedRoute レンダリング:', { permission, allowGuest });
  const auth = useAuth();
  const {
    currentUser = null,
    checkPermission = () => false,
    userRole = 'guest',
    loading = true,
    maintenanceMode = false,
    isInitialized = false
  } = auth || {};
  
  console.log('【デバッグ】ProtectedRoute 認証状態:', {
    isInitialized,
    loading,
    userRole,
    hasUser: !!currentUser,
    maintenanceMode
  });
  
  // 権限チェック
  const hasPermission = permission ? checkPermission(permission) : true;
  
  // 認証コンテキストが初期化されていない場合はローディング表示
  if (!isInitialized || loading) {
    console.log('【デバッグ】ProtectedRoute: 認証初期化中またはロード中');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ユーザーがログインしていない場合
  if (!currentUser && !allowGuest) {
    console.log('【デバッグ】ProtectedRoute: 未ログインユーザー - ログインページへリダイレクト');
    return <Navigate to="/login" replace />;
  }

  // メンテナンスモード中で、メンテナンス権限がない場合
  if (maintenanceMode && !checkPermission('MAINTENANCE_ACCESS')) {
    console.log('【デバッグ】ProtectedRoute: メンテナンスモード - メンテナンスページへリダイレクト');
    return <Navigate to="/maintenance" replace />;
  }

  // 特定の権限が必要な場合
  if (permission && !checkPermission(permission)) {
    console.log('【デバッグ】ProtectedRoute: 権限不足 - 未認証ページへリダイレクト');
    return <Navigate to="/unauthorized" replace />;
  }

  console.log('【デバッグ】ProtectedRoute: 条件をパス - 子コンポーネントを表示');
  // 認証済みで権限も問題ない場合は子コンポーネントを表示
  return children;
};

// 未認証ページの表示用コンポーネント
const PublicLayout = ({ children }) => {
  return <div className="public-layout">{children}</div>;
};

// 認証済みレイアウト
const AuthenticatedLayout = ({ children }) => {
  const auth = useAuth();
  const {
    maintenanceMode = false,
    checkPermission = () => false,
    isLoggedIn = false,
    loading = true,
    isInitialized = false
  } = auth || {};
  
  const location = useLocation();
  const isDashboardPage = location.pathname === '/dashboard';
  // 企業ユーザーダッシュボード関連のパスをチェック
  const isCorporateDashboardPage = [
    '/corporate/dashboard',
    '/corporate/advertisements',
    '/corporate/tagged-users',
    '/corporate/reservations',
    '/corporate/profile'
  ].includes(location.pathname);
  
  // 法的ページ（プライバシーポリシーなど）のチェック
  const isLegalPage = [
    '/privacy',
    '/terms',
    '/cookies',
    '/ads'
  ].includes(location.pathname);
  
  // 認証コンテキストが初期化されていない場合はローディング表示
  if (!isInitialized || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // メンテナンスモード中で権限がない場合はリダイレクト
  if (maintenanceMode && isLoggedIn && !checkPermission('MAINTENANCE_ACCESS')) {
    return <Navigate to="/maintenance" replace />;
  }
  
  // 管理者ダッシュボードページの場合はサイドバーとウィジェットを非表示
  if (isDashboardPage) {
    return (
      <div className="dashboard-container">
        {children}
      </div>
    );
  }
  
  // 法的ページの場合はサイドバーとウィジェットを非表示
  if (isLegalPage) {
    return (
      <div className="public-layout">
        {children}
      </div>
    );
  }
  
  // 企業ユーザーダッシュボードの場合はウィジェットのみ非表示
  if (isCorporateDashboardPage) {
    return (
      <div className="main-container">
        <Sidebar />
        <div className="app__body" style={{ marginRight: '0' }}>
          {children}
        </div>
        <PWAInstallBanner />
      </div>
    );
  }
  
  return (
    <div className="main-container">
      <Sidebar />
      <div className="app__body">
        {children}
      </div>
      <Suspense fallback={<Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>}>
        <Widgets />
      </Suspense>
      <PWAInstallBanner />
    </div>
  );
};

// 独自の ThemeContext(isDarkMode) を MUI テーマの palette.mode に連動させるブリッジ。
// これにより MUI の color="text.secondary" などがダークモードで自動的に見やすい配色になる。
function MuiThemeBridge({ children }) {
  const { isDarkMode } = useTheme() || {};
  const muiTheme = React.useMemo(() => createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      // contrastText を白に固定。#1DA1F2 は明るめの青で MUI の自動計算だと
      // 黒文字を選ぶことがあるため、青背景ボタンの文字を常に白にする。
      primary: { main: '#1DA1F2', contrastText: '#ffffff' },
      ...(isDarkMode
        ? {
            background: { default: '#15202b', paper: '#192734' },
            text: { primary: '#ffffff', secondary: '#8899a6' },
          }
        : {}),
    },
  }), [isDarkMode]);
  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}

// AppContent関数コンポーネントを最適化
function AppContent() {
  console.log('【デバッグ】AppContent レンダリング開始');
  const auth = useAuth();
  // 認証コンテキストがnullやundefinedではないことを確認
  const { 
    maintenanceMode = false, 
    checkPermission = () => false, 
    userRole = 'guest', 
    currentUser = null, 
    loading = true, 
    isInitialized = false,
    isLoggedIn = false,
    tokenExpired = false
  } = auth || {};
  
  console.log('【デバッグ】AppContent 認証状態:', {
    isInitialized,
    loading,
    userRole,
    hasUser: !!currentUser,
    maintenanceMode,
    isLoggedIn,
    tokenExpired
  });
  
  const location = useLocation();
  const currentPath = location.pathname;
  console.log('【デバッグ】現在のパス:', currentPath);
  
  // トークン期限切れの場合、ログインページにリダイレクト
  if (tokenExpired && currentPath !== '/login') {
    console.log('【デバッグ】トークン期限切れ検出 - ログインページにリダイレクト');
    return <Navigate to="/login" replace />;
  }
  
  // 権限チェック
  const isAdmin = userRole === 'admin' || checkPermission('ADMIN');
  const isCorporate = userRole === 'corporate' || checkPermission('POST_ADS');

  // useEffectを追加して認証状態の変更を検知
  React.useEffect(() => {
    console.log('【デバッグ】AppContent useEffect - 認証状態変更検知:', { 
      isInitialized, 
      loading, 
      userRole,
      isLoggedIn
    });
  }, [isInitialized, loading, userRole, isLoggedIn]);

  // 認証コンテキストが初期化されていない場合はローディング表示
  if (!isInitialized) {
    console.log('【デバッグ】AppContent: 認証初期化待機中');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Box sx={{ mt: 2 }}>認証初期化中...</Box>
      </Box>
    );
  }

  // ロード中の表示
  if (loading) {
    console.log('【デバッグ】AppContent: データロード中');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Box sx={{ mt: 2 }}>データ読み込み中...</Box>
      </Box>
    );
  }

  console.log('【デバッグ】AppContent: Routesコンポーネントをレンダリング');
  return (
    <>
      <ErrorNotification />
      
      <Routes>
        {/* 公開ページ（ログイン不要） */}
        <Route 
          path="/login" 
          element={
            <PublicLayout>
              <Login />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/register" 
          element={
            <PublicLayout>
              <Register />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/terms" 
          element={
            <PublicLayout>
              <Terms />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/privacy" 
          element={
            <PublicLayout>
              <Privacy />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/cookies" 
          element={
            <PublicLayout>
              <Cookies />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/verify-email/:token" 
          element={
            <PublicLayout>
              <VerifyEmail />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/reset-password/:token" 
          element={
            <PublicLayout>
              <ResetPassword />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/forgot-password" 
          element={
            <PublicLayout>
              <ForgotPassword />
            </PublicLayout>
          } 
        />

        {/* 自前の日本語メールアクションページ（メール確認・パスワード再設定の遷移先） */}
        <Route 
          path="/auth/action" 
          element={
            <PublicLayout>
              <AuthAction />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/unauthorized" 
          element={
            <PublicLayout>
              <UnauthorizedPage />
            </PublicLayout>
          } 
        />
        
        <Route 
          path="/maintenance" 
          element={
            <PublicLayout>
              <UnauthorizedPage />
            </PublicLayout>
          } 
        />
        
        {/* 認証が必要なページ */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <Home />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/explore" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <Explore />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Notifications />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/bookmarks" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Bookmarks />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/blocked-users" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <BlockedUsers />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/lists" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Lists />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/lists/:listId" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ListDetail />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/messages" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Messages />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profile/edit" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ProfileEdit />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profile/:username" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <Profile />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profile/:username/followers" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <FollowList type="followers" />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profile/:username/following" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <FollowList type="following" />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/post/:postId" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <PostDetail />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/shop" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Shop />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/rankings" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Rankings />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/reviews-ranking" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <ReviewsRanking />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/reviews-ranking/:target_id" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <ReviewDetails />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/reservations/:reservation_id" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ReservationDetail />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/my-reservations" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <PosterReservations />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/connect" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Connect />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/report/:type/:id" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ReportForm />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/report" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ReportForm />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/stats" 
          element={
            <ProtectedRoute permission="VIEW_STATS">
              <AuthenticatedLayout>
                <Stats />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Settings />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/ads" 
          element={
            <ProtectedRoute allowGuest={true}>
              <AuthenticatedLayout>
                <Ads />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/shifts" 
          element={
            <ProtectedRoute permission="POSTER_FUNCTIONS">
              <AuthenticatedLayout>
                <Shifts />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* 企業ユーザー関連のルート */}
        <Route 
          path="/corporate/dashboard" 
          element={
            <ProtectedRoute permission="POST_ADS">
              <AuthenticatedLayout>
                <CorporateDashboard />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/corporate/advertisements" 
          element={
            <ProtectedRoute permission="POST_ADS">
              <AuthenticatedLayout>
                <Advertisements />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/corporate/tagged-users" 
          element={
            <ProtectedRoute permission="POST_ADS">
              <AuthenticatedLayout>
                <TaggedUsers />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/corporate/profile" 
          element={
            <ProtectedRoute permission="POST_ADS">
              <AuthenticatedLayout>
                <CorporateProfile />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/corporate/reservations" 
          element={
            <ProtectedRoute permission="POST_ADS">
              <AuthenticatedLayout>
                <CorporateReservations />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/corporate/profile/edit" 
          element={
            <ProtectedRoute permission="POST_ADS">
              <AuthenticatedLayout>
                <CorporateProfileEdit />
              </AuthenticatedLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* 管理者専用のルート */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute permission="ADMIN_FUNCTIONS">
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/corporate-register" 
          element={
            <ProtectedRoute permission="REGISTER_CORPORATE">
              <PublicLayout>
                <CorporateRegister />
              </PublicLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/poster-register" 
          element={
            <ProtectedRoute permission="REGISTER_POSTER">
              <PublicLayout>
                <PosterRegister />
              </PublicLayout>
            </ProtectedRoute>
          } 
        />

        {/* 不明なルートは全てホームにリダイレクト */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

// App関数コンポーネント
function App() {
  console.log('【デバッグ】App コンポーネントレンダリング開始');
  
  try {
    return (
      <div className="app">
        <Router>
          <ThemeProvider>
            <MuiThemeBridge>
              <NotificationProvider>
                <SocketProvider>
                  <Suspense fallback={<LinearProgress />}>
                    <AppContent />
                  </Suspense>
                </SocketProvider>
              </NotificationProvider>
            </MuiThemeBridge>
          </ThemeProvider>
          <ToastContainer 
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </Router>
      </div>
    );
  } catch (error) {
    console.error('【デバッグ】App コンポーネントでエラー発生:', error);
    return (
      <div style={{ color: 'red', padding: '20px' }}>
        <h2>アプリケーションの初期化中にエラーが発生しました</h2>
        <p>{error.message}</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }
}

export default App; 